// Generic PDF/Excel exporter for tabular reports.
// Reuses Greek font loading logic from export-customer.js for proper Greek rendering.
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { downloadFile } from "./download";

function abToBase64(ab) {
    const bytes = new Uint8Array(ab);
    let binary = "";
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
    }
    return btoa(binary);
}

let _greekFontsCache = null;
async function loadGreekFonts() {
    if (_greekFontsCache) return _greekFontsCache;
    const [reg, bold] = await Promise.all([
        fetch("/fonts/NotoSans-Regular.ttf").then((r) => r.arrayBuffer()),
        fetch("/fonts/NotoSans-Bold.ttf").then((r) => r.arrayBuffer()),
    ]);
    _greekFontsCache = { regular: abToBase64(reg), bold: abToBase64(bold) };
    return _greekFontsCache;
}

async function makeGreekPDF(orientation = "portrait") {
    const doc = new jsPDF({ unit: "mm", format: "a4", orientation });
    try {
        const { regular, bold } = await loadGreekFonts();
        doc.addFileToVFS("NotoSans-Regular.ttf", regular);
        doc.addFont("NotoSans-Regular.ttf", "NotoSans", "normal");
        doc.addFileToVFS("NotoSans-Bold.ttf", bold);
        doc.addFont("NotoSans-Bold.ttf", "NotoSans", "bold");
        doc.setFont("NotoSans", "normal");
    } catch {
        doc.setFont("helvetica", "normal");
    }
    return doc;
}

/**
 * Export a tabular report to PDF.
 * @param {Object} opts
 * @param {string} opts.title - Main title (uppercase Greek)
 * @param {string} opts.subtitle - Period label
 * @param {Array<{label, key, format?, align?}>} opts.columns
 * @param {Array<Object>} opts.rows
 * @param {Array<{label, value}>} [opts.totals] - Footer totals
 * @param {string} opts.fileName - File name without extension
 * @param {"portrait"|"landscape"} [opts.orientation]
 */
export async function exportReportPDF({
    title,
    subtitle,
    columns,
    rows,
    totals = [],
    fileName,
    orientation = "portrait",
}) {
    const doc = await makeGreekPDF(orientation);
    const fontName = doc.getFontList().NotoSans ? "NotoSans" : "helvetica";
    const W = doc.internal.pageSize.getWidth();
    let y = 14;

    doc.setFont(fontName, "bold");
    doc.setFontSize(18);
    doc.text(title, W / 2, y, { align: "center" });
    y += 7;

    doc.setFont(fontName, "normal");
    doc.setFontSize(10);
    doc.text(`Περίοδος: ${subtitle}`, W / 2, y, { align: "center" });
    y += 5;

    doc.text(
        `Δημιουργία: ${new Date().toLocaleString("el-GR")}`,
        W / 2,
        y,
        { align: "center" }
    );
    y += 4;

    autoTable(doc, {
        startY: y + 4,
        head: [columns.map((c) => c.label)],
        body: rows.map((r) =>
            columns.map((c) =>
                c.format ? c.format(r[c.key], r) : String(r[c.key] ?? "")
            )
        ),
        foot:
            totals.length > 0
                ? [
                      columns.map((c, i) => {
                          if (i === 0) return "ΣΥΝΟΛΑ";
                          const t = totals.find((t) => t.key === c.key);
                          return t ? t.format(t.value) : "";
                      }),
                  ]
                : undefined,
        styles: { font: fontName, fontSize: 9 },
        headStyles: {
            font: fontName,
            fontStyle: "bold",
            fillColor: [10, 10, 10],
            textColor: 255,
        },
        footStyles: {
            font: fontName,
            fontStyle: "bold",
            fillColor: [240, 240, 240],
            textColor: 0,
        },
        columnStyles: columns.reduce((acc, c, i) => {
            if (c.align === "right") acc[i] = { halign: "right" };
            return acc;
        }, {}),
        margin: { left: 10, right: 10 },
    });

    const blob = doc.output("blob");
    downloadFile(blob, `${fileName}.pdf`, "application/pdf");
}

/**
 * Formats a number using Greek locale conventions (comma as decimal separator).
 * Used in Excel exports so cells display as "2,50" instead of "2.50" regardless
 * of the operator's Excel language settings.
 */
function fmtNumGR(n, decimals = 2) {
    if (n === null || n === undefined || n === "") return "";
    const num = Number(n);
    if (!Number.isFinite(num)) return "";
    return num.toLocaleString("el-GR", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        useGrouping: false,
    });
}

/**
 * Export a tabular report to Excel (xlsx).
 */
export function exportReportExcel({
    title,
    subtitle,
    columns,
    rows,
    totals = [],
    fileName,
}) {
    const wsData = [];
    wsData.push([title]);
    wsData.push([`Περίοδος: ${subtitle}`]);
    wsData.push([`Δημιουργία: ${new Date().toLocaleString("el-GR")}`]);
    wsData.push([]);
    wsData.push(columns.map((c) => c.label));
    rows.forEach((r) =>
        wsData.push(
            columns.map((c) => {
                const raw = r[c.key];
                // Numeric fields are exported as Greek-formatted strings so
                // decimals always render with the comma separator ("2,50")
                // regardless of the operator's Excel locale.
                if (c.numeric) {
                    // Detect currency vs integer count by inspecting the
                    // column's own formatter output.
                    const preview = c.format ? c.format(raw, r) : "";
                    if (/[,.]\d/.test(preview)) {
                        return fmtNumGR(raw, 2);
                    }
                    const n = Number(raw);
                    return String(Number.isFinite(n) ? Math.round(n) : 0);
                }
                return c.format ? c.format(raw, r) : raw ?? "";
            })
        )
    );
    if (totals.length > 0) {
        wsData.push(
            columns.map((c, i) => {
                if (i === 0) return "ΣΥΝΟΛΑ";
                const t = totals.find((t) => t.key === c.key);
                if (!t) return "";
                if (c.numeric) {
                    const preview = c.format ? c.format(t.value) : "";
                    if (/[,.]\d/.test(preview)) {
                        return fmtNumGR(t.value, 2);
                    }
                    const n = Number(t.value);
                    return String(Number.isFinite(n) ? Math.round(n) : 0);
                }
                return t.format(t.value);
            })
        );
    }

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    // Auto-width
    const colWidths = columns.map((c, i) => {
        const labelLen = c.label.length;
        let max = labelLen;
        rows.forEach((r) => {
            const v = c.format ? c.format(r[c.key], r) : String(r[c.key] ?? "");
            if (v.length > max) max = v.length;
        });
        return { wch: Math.min(Math.max(max + 2, 10), 40) };
    });
    ws["!cols"] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    downloadFile(
        new Blob([buf], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `${fileName}.xlsx`,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    );
}
